import RcDrawer from 'rc-drawer';
import styled, { css } from 'styled-components';

import { drawerConsts } from './Drawer.consts';
import { DrawerFooterAlignment, DrawerPaddings, DrawerSize, IDrawerProps } from './Drawer.types';
import { getFooterAlignment } from './helpers/styleHelpers';

type DrawerContentProps = {
  size: DrawerSize;
  paddings: DrawerPaddings;
  bodyPadding: boolean;
};

type DrawerBarProps = {
  size: DrawerSize;
  footerAlignment?: DrawerFooterAlignment;
  sticky: boolean;
  paddings: DrawerPaddings;
  borders: boolean;
};

const Drawer: any = styled(RcDrawer)<IDrawerProps>`
  ${({ prefixCls, theme }) => css`
    position: fixed;
    z-index: 9999;
    transition: width 0s ease 0.1s, height 0s ease 0.1s, transform 0.1s cubic-bezier(0.78, 0.14, 0.15, 0.86);
    
    > * {
      /* prettier-ignore */
      transition: transform 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86),
      opacity 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86),
        box-shadow ${theme.elevation.level2};
    }

    & .${prefixCls}-open {
      transition: transform 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
    }
    
    & .${prefixCls}-mask {
      background-color: ${theme.background.surface};
      opacity: 0;
      width: 100%;
      height: 0;
      position: absolute;
      top: 0;
      left: 0;
      transition: opacity 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86), height 0s ease 0.3s;
    }
    
    .${prefixCls}-content-wrapper {
      position: absolute;
      background-color: ${theme.background.surface};
    }
    
    .${prefixCls}-content {
      display: flex;
      flex-flow: column;
      overflow: auto;
      z-index: 1;
      position: relative;
    }
    
    &.${prefixCls}-left, &.${prefixCls}-right {
      width: 0;
      height: 100%;
      
      .${prefixCls}-content-wrapper {
        height: 100%;
      }
      
      &.${prefixCls}-open {
        width: 60%;
        
        &.no-mask {
          width: 0;
        }
      }
    }
    
    &.${prefixCls}-left {
      top: 0;
      left: 0;
      
      .${prefixCls} {
        &-handle {
          right: -40px;
          box-shadow ${theme.elevation.level2};
          border-radius: 0 4px 4px 0;
        }
      }
      
      &.${prefixCls}-open {
        .${prefixCls} {
          &-content-wrapper {
            box-shadow ${theme.elevation.level2};
          }
        }
      }
    }
    
    &.${prefixCls}-right {
      top: 0;
      right: 0;
      
      .${prefixCls} {
        &-content-wrapper {
          right: 0;
        }
        
        &-handle {
          left: -40px;
          box-shadow: ${theme.elevation.level2};
          border-radius: 4px 0 0 4px;
        }
      }
      
      &.${prefixCls}-open {
        & .${prefixCls} {
          &-content-wrapper {
            box-shadow: ${theme.elevation.level2};
          }
        }
        
        &.no-mask {
          right: 1px;
          transform: translateX(1px);
        }
      }
    }
    
    &.${prefixCls}-top, &.${prefixCls}-bottom {
      width: 100%;
      height: 0;
      
      .${prefixCls}-content-wrapper, .${prefixCls}-content {
        width: 100%;
      }
      
      .${prefixCls}-content {
        height: 100%;
      }
      
      &.${prefixCls}-open {
        height: 100%;
        
        &.no-mask {
          height: 0;
        }
      }

      .${prefixCls} {
        &-handle {
          left: 50%;
          margin-left: -20px;
        }
      }
    }
    
    &.${prefixCls}-top {
      top: 0;
      left: 0;
      
      .${prefixCls} {
        &-handle {
          top: auto;
          bottom: -40px;
          box-shadow: ${theme.elevation.level2};
          border-radius: 0 0 4px 4px;
        }
      }
      
      &.${prefixCls}-open {
        .${prefixCls} {
          &-content-wrapper {
            box-shadow: ${theme.elevation.level2};
          }
        }
      }
    }
    
    &.${prefixCls}-bottom {
      bottom: 0;
      left: 0;
      
      .${prefixCls} {
        &-content-wrapper {
          bottom: 0;
        }
        
        &-handle {
          top: -40px;
          box-shadow: ${theme.elevation.level2};
          border-radius: 4px 4px 0 0;
        }
      }
      
      &.${prefixCls}-open {
        .${prefixCls} {
          &-content-wrapper {
            box-shadow: ${theme.elevation.level2};
          }
        }
        
        &.no-mask {
          bottom: 1px;
          transform: translateY(1px);
        }
      }
    }
    
    &.${prefixCls}-open {
      .${prefixCls} {
        &-mask {
          opacity: 0.3;
          height: 100%;
          transition: opacity 0.3s cubic-bezier(0.78, 0.14, 0.15, 0.86);
        }
        
        &-handle {
          &-icon {
            background: transparent;
            
            &:before {
              transform: translateY(5px) rotate(45deg);
            }
            
            &:after {
              transform: translateY(-5px) rotate(-45deg);
            }
          }
        }
      }
    }
  `}
`;

const DrawerContent = styled.div<DrawerContentProps>`
  ${({ size, paddings, bodyPadding }) => css`
    display: flex;
    flex-direction: column;

    ${bodyPadding &&
    css`
      padding: ${drawerConsts[size][paddings].padding};
    `}
  `}
`;

const DrawerBar = styled.div<DrawerBarProps>`
  ${({ size, paddings, theme }) => css`
    z-index: 1;
    display: flex;
    align-items: center;
    min-height: ${drawerConsts[size][paddings].height};
    padding: ${drawerConsts[size][paddings].padding};
    background: ${theme.background.surface};
  `}
`;

const DrawerHeader = styled(DrawerBar)<DrawerBarProps>`
  ${({ size, borders, sticky, theme }) => css`
    position: sticky;
    top: ${sticky ? 0 : 'none'};
    ${size === 'small' && theme.heading[3].demi};
    ${size !== 'small' && theme.heading[2].demi};
    justify-content: space-between;

    ${borders &&
    css`
      border-bottom: 1px solid ${theme.border.medium};
    `}
  `}
`;

const DrawerFooter = styled(DrawerBar)<DrawerBarProps>`
  ${({ borders, sticky, footerAlignment, theme }) => css`
    position: sticky;
    bottom: ${sticky ? 0 : 'none'};
    justify-content: ${getFooterAlignment(footerAlignment)};

    ${borders &&
    css`
      border-top: 1px solid ${theme.border.medium};
    `}
  `}
`;

export { Drawer, DrawerContent, DrawerHeader, DrawerFooter };
